/**
 * Installs `window.livingsyncs` when running inside the Capacitor iPad shell,
 * implementing the SAME LivingSyncsBridge contract the Electron preload
 * implements (see ./platform.ts).
 *
 * Why this lives in the frontend rather than the mobile shell repo: the iPad
 * app bundles this SPA as its Capacitor `webDir`, so the SPA's own JS is what
 * actually runs in the web view. There is no Electron-style preload step for
 * native to inject a bridge from — Capacitor injects only `window.Capacitor`.
 *
 * NO LONGER RELIES ON IMPORT ORDER (fixed 2026-09-12) — platform.ts's
 * `desktop` used to be a plain `const` snapshotted once at THAT module's own
 * load time, which only worked if this module was guaranteed to evaluate
 * first. That guarantee held for main.tsx's literal source order, but broke
 * once Rollup's automatic chunking split platform.ts into a separate chunk
 * from this file (it's shared with useAuth.ts, also reachable from
 * lazy-loaded routes) — confirmed live, `desktop` was still frozen undefined
 * after `window.livingsyncs` was already set. `refreshDesktopBridge()` below
 * makes this explicit instead of implicit: call it right after actually
 * setting the global, and every consumer is correct regardless of chunk
 * evaluation order. See platform.ts's own comment on `desktop` for the full
 * mechanism.
 *
 * On the web this is inert: `window.Capacitor` is undefined, nothing is
 * installed, `isElectron` stays false, and every consumer behaves exactly as
 * before.
 */
import type { LivingSyncsBridge } from "./platform";
// Deep import, not the barrel — see gotcha_packages_barrel_breaks_every_consumer_build.
// packages/ui/src/lib/platform.ts has zero imports of its own, so this
// introduces no further evaluation-order risk.
import { refreshDesktopBridge } from "@livingsyncs/ui/src/lib/platform";
import { keychainTokenStore } from "./native/keychainTokenStore";

// `Window.Capacitor` is declared once, in ./platform — the canonical seam
// module — so the two files cannot drift.

const noop = (): void => {};

/**
 * The shell communicates with this page over CustomEvents on `window`, since
 * both run in the same web view but are built separately.
 */
function subscribeToShellEvent<T = void>(
  name: string,
  cb: (detail: T) => void,
): () => void {
  const handler = (event: Event): void => {
    cb((event as CustomEvent<T>).detail);
  };
  window.addEventListener(name, handler);
  return () => window.removeEventListener(name, handler);
}

function emitShellEvent(name: string, detail?: unknown): void {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

export function installCapacitorBridge(): boolean {
  if (typeof window === "undefined") return false;
  if (!window.Capacitor?.isNativePlatform()) return false;

  // Never clobber an existing bridge — the Electron preload installs its own,
  // and overwriting it would silently disable desktop's real token store.
  if (window.livingsyncs) return false;

  const bridge: LivingSyncsBridge = {
    // Same baked value the web build uses. apiClient.ts reads
    // `desktop?.backendUrl || import.meta.env.VITE_API_URL || …`, so this is
    // belt-and-braces rather than load-bearing today; it becomes the override
    // point if the shell ever gains a runtime backend config.
    backendUrl: (import.meta.env.VITE_API_URL as string) ?? "",
    getPlatform: () => "ios",
    // US-183: `VITE_APP_VERSION` is baked in by the mobile release
    // pipeline's "Resolve app version" step (mobile-release.yml), sourced
    // from mobile/package.json — NOT frontend/package.json, which is a
    // different, unrelated version on the continuous web-deploy cadence.
    // Falls back to "0.0.0" in dev/test, where nothing sets the var and
    // nothing should read this as a real version.
    getAppVersion: () =>
      (import.meta.env.VITE_APP_VERSION as string) ?? "0.0.0",
    // US-015 AC10 — real Keychain-backed storage; `available` is false (and
    // Amplify keeps its own default store) whenever the native plugin isn't
    // registered, i.e. on web and inside the Electron desktop shell.
    tokenStore: keychainTokenStore,

    // iPadOS has no user-visible filesystem to "save as" into; the SPA's
    // normal browser download path is the correct behaviour.
    saveExport: async () => ({ saved: false }),

    // US-015 AC12 (partial) — clears the Keychain token store on sign-out,
    // the same hook apiClient.ts's logoutAndSignOut() calls for the Electron
    // shell (US-013 AC12). WKWebsiteDataStore clearing is AC12's other half
    // and stays out of scope for AC10 — see the governing plan.
    purge: async () => {
      await keychainTokenStore.clear();
    },

    // US-176 Phase 2 — this field exists purely to satisfy LivingSyncsBridge's
    // shared shape; iOS's device-bound re-auth credential goes through
    // Capacitor's OWN registerPlugin("KeychainTokenStore") mechanism instead
    // (packages/ui/src/lib/native/reauthCredential.ts), never through
    // window.livingsyncs — that file only reaches these methods when
    // `isElectron` is true, which is never the case here. Throwing (not a
    // silent no-op) is deliberate: if that gating logic ever has a bug and
    // calls this on iPad anyway, the failure should be loud, matching this
    // same feature's own reportAndRethrow convention elsewhere.
    reauthCredential: {
      available: false,
      mint: () =>
        Promise.reject(
          new Error(
            "reauthCredential.mint is unreachable on Capacitor — iOS uses its own native plugin",
          ),
        ),
      sign: () =>
        Promise.reject(
          new Error(
            "reauthCredential.sign is unreachable on Capacitor — iOS uses its own native plugin",
          ),
        ),
      exists: () => Promise.resolve({ exists: false }),
      clear: () => Promise.resolve(),
    },

    onLock: (cb) => subscribeToShellEvent("ls:lock", cb),
    onSignOut: (cb) => subscribeToShellEvent("ls:signout", cb),
    onDeepLink: (cb) => subscribeToShellEvent<string>("ls:deeplink", cb),

    // MDM owns binary updates on iPadOS — there is nothing to download or
    // install in-app. No-ops rather than widening the shared type.
    onUpdateDownloaded: () => noop,
    onUpdateError: () => noop,
    quitAndInstall: noop,

    showNotification: (payload) => emitShellEvent("ls:notify", payload),
    setBadgeCount: (count) => emitShellEvent("ls:badge", count),
  };

  window.livingsyncs = bridge;
  // Correct every consumer's `desktop`/`isElectron`/`isNative` NOW, regardless
  // of whether platform.ts's module happened to already evaluate (and freeze
  // `desktop` undefined) before this point — see this file's own header
  // comment and platform.ts's comment on `desktop`.
  refreshDesktopBridge();
  return true;
}

// Side effect on import — see the ordering note above.
installCapacitorBridge();
